import * as yup from 'yup'
import React from 'react'
import { suffixes } from '../choices'
import { Divider } from '../components/divider'

import {
  addressLabel,
  address2Label,
  aliasLabel,
  cellPhoneLabel,
  dobLabel,
  firstNameLabel,
  homeAddressLabel,
  homePhoneLabel,
  lastNameLabel,
  medicareNumberLabel,
  medicaidNumberLabel,
  middleNameLabel,
  optionalLabel,
  personalEmailLabel,
  ssnLabel,
  suffixLabel
} from '../strings/labels'
import {
  addressLengthMessage,
  cellPhoneMessage,
  cityLengthMessage,
  firstNameMessage,
  firstNameLengthMessage,
  lastNameMessage,
  lastNameLengthMessage,
  maxLength150Message,
  medicareNumberMessage,
  medicaidNumberMessage,
  middleNameMessage,
  middleNameLengthMessage,
  personalEmailMessage,
  personalEmailLengthMessage,
  personalPhoneMessage,
  ssnMessage,
  zipMessage,
  zipLengthMessage
} from '../strings/messages'
import {
  alphaRegex,
  medicareNumberRegex,
  medicaidNumberRegex,
  phoneRegex,
  ssnRegex,
  zipRegex
} from '../strings/regex'
import { dateYupOptional } from '../validators/dateYup'

const VictimNameHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Victim name</h2>
)

const HomeAddressHeading = () => (
  <h2 className="ds-u-margin-bottom--0">{homeAddressLabel}</h2>
)

const ContactInformationHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Contact information</h2>
)

export const victimPage = () => ({
  id: 'victim_page',
  title: 'Victim information',
  description:
    'Victim information is necessary for us to conduct an investigation. Please provide as much detail as you can.',
  validation: yup.object().shape({
    victim_first_name: yup
      .string()
      .matches(alphaRegex, {
        message: firstNameMessage,
        excludeEmptyString: false
      })
      .max(20, firstNameLengthMessage)
      .required(firstNameMessage),
    victim_middle_name: yup
      .string()
      .matches(alphaRegex, {
        message: middleNameMessage,
        excludeEmptyString: false
      })
      .max(20, middleNameLengthMessage),
    victim_last_name: yup
      .string()
      .matches(alphaRegex, {
        message: lastNameMessage,
        excludeEmptyString: false
      })
      .max(30, lastNameLengthMessage)
      .required(lastNameMessage),
    victim_alias_nickname: yup.string().max(150, maxLength150Message),
    victim_ssn: yup.string().matches(ssnRegex, {
      message: ssnMessage,
      excludeEmptyString: false
    }),
    victim_email: yup
      .string()
      .email(personalEmailMessage)
      .max(250, personalEmailLengthMessage),
    victim_personal_phone: yup.string().matches(phoneRegex, {
      message: personalPhoneMessage,
      excludeEmptyString: false
    }),
    victim_alternative_phone: yup.string().matches(phoneRegex, {
      message: cellPhoneMessage,
      excludeEmptyString: false
    }),
    victim_address: yup.object().shape({
      line1Value: yup.string().max(50, addressLengthMessage),
      line2Value: yup.string().max(50, addressLengthMessage),
      cityValue: yup.string().max(30, cityLengthMessage),
      zipValue: yup
        .string()
        .max(10, zipLengthMessage)
        .test('zipcode_required', zipMessage, function(value) {
          const { countryValue } = this.parent
          return countryValue === 'US'
            ? value === '' || zipRegex.test(value)
            : true
        })
    }),
    victim_medicare_number: yup.string().matches(medicareNumberRegex, {
      message: medicareNumberMessage,
      excludeEmptyString: true
    }),
    victim_medicaid_number: yup.string().matches(medicaidNumberRegex, {
      message: medicaidNumberMessage,
      excludeEmptyString: true
    }),
    victim_dob: dateYupOptional()
  }),
  fields: [
    {
      type: 'content',
      content: VictimNameHeading,
      name: 'victim_name'
    },
    {
      type: 'text',
      label: firstNameLabel,
      name: 'victim_first_name'
    },
    {
      type: 'text',
      label: middleNameLabel,
      name: 'victim_middle_name',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: lastNameLabel,
      name: 'victim_last_name'
    },
    {
      type: 'select',
      label: suffixLabel,
      name: 'victim_suffix',
      choices: [
        {
          value: '',
          label: ''
        },
        ...suffixes()
      ],
      requirementLabel: optionalLabel
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider_name'
    },
    {
      type: 'text',
      label: aliasLabel,
      name: 'victim_alias_nickname',
      requirementLabel: optionalLabel
    },
    {
      type: 'date',
      label: dobLabel,
      name: 'victim_dob',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: medicareNumberLabel,
      name: 'victim_medicare_number',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: medicaidNumberLabel,
      name: 'victim_medicaid_number',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: ssnLabel,
      name: 'victim_ssn',
      requirementLabel: optionalLabel
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider_contact'
    },
    {
      type: 'content',
      content: ContactInformationHeading,
      name: 'contact_information'
    },
    {
      type: 'text',
      label: homePhoneLabel,
      name: 'victim_personal_phone',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: cellPhoneLabel,
      name: 'victim_alternative_phone',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: personalEmailLabel,
      name: 'victim_email',
      requirementLabel: optionalLabel
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider1'
    },
    {
      type: 'content',
      content: HomeAddressHeading,
      name: 'mailing_address'
    },
    {
      type: 'address',
      name: 'victim_address',
      label: '',
      hint: '',
      line1Label: addressLabel,
      line2Label: address2Label,
      line1RequirementLabel: optionalLabel,
      line2RequirementLabel: optionalLabel,
      cityRequirementLabel: optionalLabel,
      stateRequirementLabel: optionalLabel,
      zipRequirementLabel: optionalLabel,
      countryValue: 'US'
    }
  ]
})
